import React, { useState, memo, useCallback, useMemo } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { PostCard } from "../PostCard";
import { PostContentSection, PostOverlay } from "@/components/PostCard";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SchedulePostModal } from "../SchedulePostModal";
import { PublishModal } from "../PublishModal";
import { ScheduleErrorDisplay } from "../ScheduleErrorDisplay";
import { useAppStore } from "@/core/store/useAppStore";
import { postCacheUtils } from "../hooks/usePostsQuery";
import { useDeletePost } from "../hooks/useDeletePost";
import { usePostStatus } from "../hooks/usePostStatus";
import { copyToClipboard } from "@/utils/clipboard";
import { queryClient } from "@/core/queryClient";
import {
  CalendarCheckIcon,
  ShareIcon,
  SendIcon,
  TrashIcon,
  DotsVerticalIcon,
  ErrorIcon,
  CopyIcon,
  CheckIcon,
} from "@/components/icons";
import { cn } from "@/utils/cn";
import { isStoryDurationValid } from "./isStoryDurationValid";
import { needsPromoteBeforeSchedule } from "./needsPromoteBeforeSchedule";

interface StoriesCardProps {
  post: Post;
  onPostDeleted: () => void;
  /** Extra classes on the card container (e.g. `border-0` inside panels). */
  className?: string;
}

// The Stories board caches under its own key (see hooks/useStories.ts). Card
// actions default to writing the FEED cache via postCacheUtils, so we must
// ALSO invalidate the stories query — otherwise a schedule/publish/delete
// leaves the story grid stale until a manual refresh.
const STORY_QUERY_ROOT = "stories" as const;
const invalidateStories = (clientId: string) => {
  queryClient.invalidateQueries({ queryKey: [STORY_QUERY_ROOT, clientId] });
};

/**
 * Stories board card. Mirrors EditorPostCard but TRIMMED to the story flow:
 * a 3-dot context menu with only Schedule/Reschedule, Publish now, and
 * Delete. No full status dropdown (which would also let a story slip to an
 * off-board status), no caption/boost/public/commercial actions.
 *
 * Adds a duration guard: story videos must run 3–60s. Outside that range a
 * warning pill renders and the Schedule/Publish menu items are disabled.
 *
 * Used by: StoriesView.tsx.
 */
export const StoriesCard: React.FC<StoriesCardProps> = memo(
  ({ post, onPostDeleted, className }) => {
    const { selectedClient } = useAppStore();
    const addToast = useAppStore((s) => s.addToast);

    const [isHovered, setIsHovered] = useState(false);
    const [copiedPostId, setCopiedPostId] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { deletePost, isDeleting } = useDeletePost({
      onSuccess: () => {
        if (selectedClient?.id) invalidateStories(selectedClient.id);
        onPostDeleted();
      },
      // Surface backend rejections (e.g. post locked in a grid); otherwise the
      // dialog just closes and the story silently stays put.
      onError: (message) => {
        addToast({ type: "error", message, duration: 6000 });
      },
    });

    const { updateStatus, isUpdating } = usePostStatus({
      onError: (message) => {
        addToast({ type: "error", message, duration: 6000 });
      },
    });

    // Story duration lives on the first media item's durationSeconds. Null /
    // undefined (image story, or duration not yet extracted) is treated as
    // valid — the guard only fires on a concrete out-of-range value.
    const dur = post.mediaItems?.[0]?.durationSeconds ?? null;
    const durationValid = isStoryDurationValid(dur);
    const durationWarning =
      dur != null && dur > 60
        ? "Video too long — max 60s"
        : dur != null && dur < 3
          ? "Video too short — min 3s"
          : null;

    // Send for review is offered only from DRAFT — it moves the story into the
    // client-approval stage (APPROVAL_PENDING), which surfaces it in the
    // client portal server-side.
    const canSendForReview = post.status === PostStatus.DRAFT;
    // DRAFT and APPROVAL_PENDING stories must be schedulable too: auto-created
    // stories land in DRAFT, and the schedule handler promotes them to READY
    // before opening the modal (the /schedule endpoint gates on READY/SCHEDULED).
    const canSchedule =
      post.status === PostStatus.DRAFT ||
      post.status === PostStatus.APPROVAL_PENDING ||
      post.status === PostStatus.READY ||
      post.status === PostStatus.SCHEDULED;
    // Publish now is offered from draft/review too; handlePublishNow promotes to
    // READY first (which triggers server-side preparation) before opening the
    // publish modal.
    const canPublish =
      post.status === PostStatus.DRAFT ||
      post.status === PostStatus.APPROVAL_PENDING ||
      post.status === PostStatus.READY ||
      post.status === PostStatus.SCHEDULED ||
      post.status === PostStatus.PARTIALLY_PUBLISHED;

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const handleDeleteConfirm = useCallback(async () => {
      setShowDeleteConfirm(false);
      await deletePost(post.id);
    }, [deletePost, post.id]);

    // A DRAFT story can't hit the /schedule endpoint (it gates on
    // READY/SCHEDULED). Promote to READY first, then open the modal. Bail out
    // of opening the modal if the promotion fails so we don't schedule a post
    // the backend will reject.
    const handleSchedule = useCallback(async () => {
      if (needsPromoteBeforeSchedule(post.status)) {
        const response = await updateStatus(post.id, PostStatus.READY);
        if (!response) return;
      }
      setShowScheduleModal(true);
    }, [post.status, post.id, updateStatus]);

    // Move a DRAFT story into the client-approval stage. The backend
    // client-review flow is post-type-agnostic, so an APPROVAL_PENDING story
    // already shows up in the client portal — no extra call needed here.
    const handleSendForReview = useCallback(async () => {
      await updateStatus(post.id, PostStatus.APPROVAL_PENDING);
    }, [post.id, updateStatus]);

    // Publish now from any stage. If the story isn't already publishable,
    // promote it to READY first (which triggers server-side preparation) and
    // bail if that fails, then open the publish modal. The modal's per-platform
    // buttons stay disabled until preparation reports ready.
    const handlePublishNow = useCallback(async () => {
      const alreadyPublishable =
        post.status === PostStatus.READY ||
        post.status === PostStatus.SCHEDULED ||
        post.status === PostStatus.PARTIALLY_PUBLISHED;
      if (!alreadyPublishable) {
        const response = await updateStatus(post.id, PostStatus.READY);
        if (!response) return;
      }
      setShowPublishModal(true);
    }, [post.status, post.id, updateStatus]);

    const contextMenuItems = useMemo((): DropdownMenuEntry[] => {
      const items: DropdownMenuEntry[] = [];

      if (canSendForReview) {
        items.push({
          id: "send-for-review",
          label: "Send for review",
          icon: <SendIcon className="w-4 h-4" />,
          onClick: () => {
            void handleSendForReview();
          },
          disabled: isUpdating,
        });
      }

      if (canSchedule) {
        items.push({
          id: "schedule",
          label:
            post.status === PostStatus.SCHEDULED ? "Reschedule" : "Schedule",
          icon: <CalendarCheckIcon className="w-4 h-4" />,
          onClick: () => {
            void handleSchedule();
          },
          disabled: !durationValid || isUpdating,
        });
      }

      if (canPublish) {
        items.push({
          id: "publish",
          label: "Publish now",
          icon: <ShareIcon className="w-4 h-4" />,
          onClick: () => {
            void handlePublishNow();
          },
          disabled: !durationValid || isUpdating,
        });
      }

      items.push({ id: "separator", separator: true as const });
      items.push({
        id: "delete",
        label: "Delete",
        icon: <TrashIcon className="w-4 h-4" />,
        onClick: () => setShowDeleteConfirm(true),
        variant: "danger" as const,
      });

      return items;
    }, [
      canSendForReview,
      canSchedule,
      canPublish,
      post.status,
      durationValid,
      isUpdating,
      handleSendForReview,
      handleSchedule,
      handlePublishNow,
    ]);

    const overlayTopRight = isHovered ? (
      <PostOverlay.TopRight>
        <DropdownMenu
          items={contextMenuItems}
          width="w-48"
          menuClassName="rounded-lg py-1"
          trigger={
            <button
              className={cn(
                "p-1.5 rounded-full shadow-xs transition-all hover:shadow-md",
                "bg-white/90 hover:bg-white text-gray-700",
              )}
              title="Actions"
              data-interactive="true"
            >
              <DotsVerticalIcon className="w-4 h-4" />
            </button>
          }
        />
      </PostOverlay.TopRight>
    ) : undefined;

    return (
      <>
        <PostCard
          post={post}
          viewMode="grid"
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          overlayTopRight={overlayTopRight}
        >
          <PostContentSection viewMode="grid">
            {/* Header row: Copy ID + duration warning */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  copyToClipboard(post.id);
                  setCopiedPostId(true);
                  setTimeout(() => setCopiedPostId(false), 2000);
                }}
                className={`p-1 rounded-md transition-colors ${
                  copiedPostId
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                }`}
                title={copiedPostId ? "Copied!" : `Copy ID: ${post.id}`}
              >
                {copiedPostId ? (
                  <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                  <CopyIcon className="w-3.5 h-3.5" />
                )}
              </button>

              {durationWarning && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full"
                  title={durationWarning}
                >
                  <ErrorIcon className="w-3 h-3" />
                  {durationWarning}
                </span>
              )}
            </div>

            {/* Group name */}
            {post.groupName && (
              <div className="mt-2 text-xs text-gray-500 font-medium truncate">
                {post.groupName}
              </div>
            )}

            {/* Schedule error display */}
            <div className="mt-2">
              <ScheduleErrorDisplay post={post} />
            </div>
          </PostContentSection>
        </PostCard>

        {showScheduleModal && selectedClient && (
          <SchedulePostModal
            post={post}
            clientId={selectedClient.id}
            onClose={() => setShowScheduleModal(false)}
            onScheduled={() => {
              setShowScheduleModal(false);
              postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
              invalidateStories(selectedClient.id);
            }}
          />
        )}

        {showPublishModal && selectedClient && (
          <PublishModal
            post={post}
            clientId={selectedClient.id}
            onClose={() => {
              setShowPublishModal(false);
              invalidateStories(selectedClient.id);
            }}
          />
        )}

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Story"
          message="Are you sure you want to delete this story? This action cannot be undone."
          confirmText="Delete"
          variant="danger"
          isLoading={isDeleting}
        />
      </>
    );
  },
);

StoriesCard.displayName = "StoriesCard";
